/**
 * What an element picker offers, and the listing it offers it from - pure,
 * apart from the one hook, so it can be tested without rendering a picker.
 * The component and why it is shaped this way: ElementPicker.tsx (issue #49).
 */

import { useEffect, useState } from 'react'
import { PAGE_LIMIT, api } from '../../api'
import { findKind } from '../../schema'
import type { EntityRow, Schema } from '../../schema'

/** One line of the list. */
export interface PickerOption {
  /** What is written: an identifier, a qualified name, a reference. */
  value: string
  /** The option's main line. Defaults to `value`. */
  label?: string
  /** An identifier beside the label, in monospace, when the label is a name. */
  code?: string
  /** A second line in monospace - usually the qualified name. */
  detail?: string
  /** A second line in plain text. */
  note?: string
  /** A warning chip, e.g. 'generic'. */
  tag?: string
  /** Indent, for a list that is a tree. */
  depth?: number
  /** What the box shows once this is chosen, when not the picker's `show`. */
  chosen?: string
}

/**
 * Whether `qualifiedName` sits inside `pkg`, at any depth.
 *
 * On whole `::` segments: `EXAMPLE::Design` does not contain
 * `EXAMPLE::DesignReview::x`, which a bare prefix test would say it does.
 */
export function inPackageTree(qualifiedName: string, pkg: string): boolean {
  if (!pkg) return true
  return qualifiedName.startsWith(`${pkg}::`)
}

/** The rows a picker offers: the package subtree, then any other rule. */
export function narrow(
  rows: EntityRow[],
  { inPackage = '', where }: { inPackage?: string; where?: (row: EntityRow) => boolean } = {},
): EntityRow[] {
  return rows.filter(
    (row) => inPackageTree(String(row.qualified_name ?? ''), inPackage) && (!where || where(row)))
}

/**
 * The default line for an element: its name, its identifier beside it, its
 * qualified name under it.
 *
 * The NAME is not the key - the Example has `part def <GNC> Guidance` and a
 * usage also named Guidance - which is why the identifier is always on show.
 */
export function elementOption(row: EntityRow, value: string = row.id): PickerOption {
  const name = String(row.name ?? '')
  return {
    value,
    label: name || row.id,
    code: name && name !== row.id ? row.id : undefined,
    detail: String(row.qualified_name ?? '') || undefined,
    tag: row.is_abstract ? 'generic' : undefined,
  }
}

/** The values not among `options` - what `strict` flags. */
export function unlisted(values: string[], options: PickerOption[]): string[] {
  const offered = new Set(options.map((option) => option.value))
  return values.filter((value) => value && !offered.has(value))
}

/**
 * The listing a kind is served at: the schema's plural when there is a schema,
 * and `kind` taken as the plural when there is not.
 */
export function pluralOf(schema: Schema | undefined, kind: string | undefined): string | undefined {
  if (!kind) return undefined
  return schema ? findKind(schema, kind)?.plural : kind
}

/** A kind's whole listing, as the picker and its callers need it. */
export interface Elements {
  rows: EntityRow[]
  loading: boolean
  /** The server holds more than it sent: a local filter cannot see the rest. */
  truncated: boolean
  /**
   * The WHOLE kind is in `rows`: loaded, not cut short, not failed. Only then
   * can "not among these" be said to mean "not in the model".
   */
  complete: boolean
}

/**
 * Load one kind's listing, once.
 *
 * Exported so a screen with many pickers of one kind - a table, one per row -
 * loads it once and hands each picker `rows`.
 */
export function useElements(
  schema: Schema | undefined,
  kind: string | undefined,
  serverFilter: Record<string, string> = {},
): Elements {
  const plural = pluralOf(schema, kind)
  const filterKey = JSON.stringify(serverFilter)
  const [state, setState] = useState<Elements>(
    { rows: [], loading: Boolean(plural), truncated: false, complete: false })

  useEffect(() => {
    if (!plural) {
      setState({ rows: [], loading: false, truncated: false, complete: false })
      return
    }
    let live = true
    setState((current) => ({ ...current, loading: true, complete: false }))
    api
      .list(plural, { limit: PAGE_LIMIT, ...JSON.parse(filterKey) })
      .then((page) => {
        if (!live) return
        const truncated = page.total > page.items.length
        setState({ rows: page.items, loading: false, truncated, complete: !truncated })
      })
      .catch(() => live && setState({ rows: [], loading: false, truncated: false, complete: false }))
    return () => {
      live = false
    }
  }, [plural, filterKey])

  return state
}
